import { useState } from 'react';
import { FinalReportReason } from '@signalvote/shared';
import { InlineSpinner } from '@/components/ui/inline-spinner';
import { useSetFinalReportReason } from '@/hooks/queries/useFinanceReports';

interface FinalReportEditorProps {
  reportId: string;
  finalReportReason: FinalReportReason | null;
  concurrencyVersion: number;
  /**
   * Only a pre-filing (Ready) report's final-report flag is editable: it rides the report onto its
   * amendment and checks the CVR cover box, so a filed period freezes it (reopen to correct). The
   * parent renders it read-only otherwise.
   */
  editable: boolean;
}

// Treasurer-facing copy for each reason. A Record (not a switch) so it is compile-time exhaustive:
// a new FinalReportReason without a label is a type error here.
const REASON_LABELS: Record<FinalReportReason, string> = {
  [FinalReportReason.Withdrawn]: 'Withdrawn',
  [FinalReportReason.Unopposed]: 'Unopposed',
  [FinalReportReason.FiledAfterElection]: 'Filed after the election (all debts paid, no surplus)',
};

const NOT_FINAL = '';

/**
 * GitHub #778 — mark a Louisiana report the committee's FINAL report and pick which of the three
 * mutually-exclusive reasons applies (or clear it). Saving on select-change; the reasons are
 * single-select, so their mutual exclusivity is structural. The write carries the report's
 * concurrency token (a stale token is a 409) and is audit-logged server-side.
 */
export function FinalReportEditor({
  reportId,
  finalReportReason,
  concurrencyVersion,
  editable,
}: FinalReportEditorProps) {
  const { setFinalReportReason } = useSetFinalReportReason();
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const currentLabel = finalReportReason
    ? REASON_LABELS[finalReportReason]
    : 'Not a final report';

  if (!editable) {
    return (
      <p className="text-sm font-medium" data-testid="final-report-display">
        {currentLabel}
      </p>
    );
  }

  const handleChange = async (value: string) => {
    setErrorMessage(null);
    const next = value === NOT_FINAL ? null : (value as FinalReportReason);
    setSaving(true);
    try {
      await setFinalReportReason({
        reportId,
        data: { finalReportReason: next, concurrencyVersion },
      });
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : 'Failed to update the final-report flag');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-1" data-testid="final-report-editor">
      <div className="flex items-center gap-2">
        <select
          data-testid="final-report-select"
          value={finalReportReason ?? NOT_FINAL}
          disabled={saving}
          onChange={(e) => void handleChange(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <option value={NOT_FINAL}>Not a final report</option>
          {Object.values(FinalReportReason).map((reason) => (
            <option key={reason} value={reason}>
              {REASON_LABELS[reason]}
            </option>
          ))}
        </select>
        {saving && <InlineSpinner label="Saving" className="h-3.5 w-3.5" />}
      </div>
      {errorMessage && (
        <span className="text-sm text-destructive" data-testid="final-report-error">
          {errorMessage}
        </span>
      )}
      <p className="text-xs text-muted-foreground">
        Marking a report final checks the final-report box on the cover — how a candidate signals
        their last report. Set it only on the committee's final filing.
      </p>
    </div>
  );
}
